import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Container,
  Card,
  ListGroup,
  Spinner,
  Alert,
  Button,
  Col,
  Row,
  Modal,
  Table,
} from "react-bootstrap";
import { supabase, getSafeSession, getCachedSession, clearSupabaseAuthStorage } from "../supabaseClient"; // Ensure this path is correct
import { useNavigate } from "react-router-dom";

import "./ScanHistory.css";

import AIReadinessGauge from "./Components/AIReadinessGauge";

const ScanHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tokenRef = useRef(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const handleViewDetails = async (scanEntry) => {
    let finalScan = scanEntry;
    
    // Check if the scan is stripped (i.e. older scan) and needs a full detail fetch
    if (finalScan && finalScan.id && (!finalScan.scan_data?.raw || Object.keys(finalScan.scan_data.raw).length <= 3)) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/scan_history/${finalScan.id}`,
            {
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            }
          );
          if (response.data) {
            finalScan = response.data;
          }
        }
      } catch (err) {
        console.warn("Failed to fetch full scan details, showing partial data:", err);
      }
    }
    
    setSelectedScan(finalScan);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedScan(null);
  };

  useEffect(() => {
    const fetchSessionAndHistory = async () => {
      setLoading(true);
      setError(null); // Clear previous errors

      try {
        // 1. Get the current Supabase session to retrieve the access token
        let session = getCachedSession();
        const isCachedExpired = session?.expires_at
          ? session.expires_at < Math.floor(Date.now() / 1000)
          : true;

        let sessionError = null;

        // Only perform network getSafeSession if we don't have a cached session or it is expired
        if (!session || isCachedExpired) {
          try {
            const result = await getSafeSession(15000);
            if (result.data?.session) {
              session = result.data.session;
            }
            sessionError = result.error;
          } catch (err) {
            sessionError = err;
          }
        }

        if (!session) {
          const storageKey = Object.keys(localStorage).find(key => key.startsWith("sb-") && key.endsWith("-auth-token"));
          if (!storageKey) {
            navigate("/auth");
          } else {
            console.warn("ScanHistory: getSafeSession timed out/failed, and fallback failed.");
            setError("Session loading timed out. Please check your network and refresh.");
          }
          return;
        }

        const isExpired = session.expires_at ? (session.expires_at < Math.floor(Date.now() / 1000)) : true;
        if (sessionError && isExpired) {
          console.warn("ScanHistory: Session is expired and token refresh timed out. Redirecting to /auth.");
          clearSupabaseAuthStorage();
          navigate("/auth");
          return;
        }

        const userToken = session.access_token;
        tokenRef.current = userToken;

        // 2. Make the API call to your backend
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/scan_history`,
          {
            headers: {
              Authorization: `Bearer ${userToken}`, // Attach the JWT token
            },
          },
        );

        // 3. Set the fetched history data
        setHistory(response.data);
      } catch (err) {
        console.error("Failed to fetch scan history:", err);
        let errorMessage =
          "An unexpected error occurred while fetching history.";

        if (axios.isAxiosError(err) && err.response) {
          // Backend returned an error response
          if (err.response.status === 401) {
            errorMessage =
              err.response.data.detail ||
              "Your session has expired. Please sign in again.";
            navigate("/auth"); // Redirect on unauthorized
          } else {
            errorMessage =
              err.response.data.detail ||
              `Error: ${err.response.status} - ${err.response.statusText}`;
          }
        } else if (axios.isAxiosError(err) && err.request) {
          // Request was made but no response received
          errorMessage = "Network error: Could not connect to the server.";
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionAndHistory();

    const {
      data: { subscription: authListener },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => {
        if (event === "SIGNED_IN") {
          // If a new session, re-fetch history
          if (session?.access_token && session.access_token !== tokenRef.current) {
            fetchSessionAndHistory();
          }
        } else if (event === "TOKEN_REFRESHED" && session) {
          // Update token reference without re-fetching data to prevent deadlocks
          if (session.access_token) {
            tokenRef.current = session.access_token;
          }
        } else if (event === "SIGNED_OUT") {
          // Clear history and redirect if signed out
          setHistory([]);
          tokenRef.current = null;
          navigate("/auth");
        }
      }, 0);
    });

    // Cleanup listener on component unmount
    return () => {
      authListener?.unsubscribe();
    };
  }, [navigate]); // Re-run only if navigate changes

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading history...</span>
        </Spinner>
        <p className="mt-3">Loading scan history...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/scanner")}>
          Go to Scanner
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Container className="my-5">
        <h2 className="mb-4 text-center">Your Scan History</h2>
        {history.length === 0 ? (
          <Alert variant="info" className="text-center">
            No scan history found. Start a new scan!
            <div className="mt-3">
              <Button variant="primary" onClick={() => navigate("/scanner")}>
                Go to Scanner
              </Button>
            </div>
          </Alert>
        ) : (
          <div className="row row-cols-1 row-cols-md-3 g-4">
            {history
              .filter(
                (entry) =>
                  entry && entry.scan_data && entry.scan_data.score !== null,
              )
              .map((entry) => (
                <div className="col" key={entry.id}>
                  <Card className="h-100 shadow-sm">
                    <Card.Header className="card_head text-white">
                      <h5 className="mb-0">{entry.website}</h5>
                    </Card.Header>
                    <Card.Body>
                      <Card.Text>
                        <strong>Company:</strong>{" "}
                        {entry?.company || entry.scan_data?.company || "N/A"}{" "}
                        <br />
                        <strong>Scanned On:</strong>{" "}
                        {new Date(entry.created_at).toLocaleString()}
                      </Card.Text>
                      <Card.Title className="text-center mt-3">
                        AI Score:{" "}
                        <span className="fw-bold text-success">
                          {entry.scan_data?.score}/100
                        </span>
                      </Card.Title>
                    </Card.Body>

                    <Card.Footer>
                      <div className="row row-cols-1 row-cols-md-1 g-4">
                        <div className="p-2">
                          <Button
                            className="btn-primary w-100"
                            onClick={() => handleViewDetails(entry)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </Card.Footer>
                  </Card>
                </div>
              ))}
          </div>
        )}
      </Container>

      <Modal show={showDetails} onHide={handleCloseDetails} size="lg" centered>
        <Modal.Header closeButton className="ai-bg text-white">
          <Modal.Title>Scan Details: {selectedScan?.website}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "white", color: "black" }}>
          {selectedScan && (
            <>
              {/* AI Readiness Score Gauge */}
              <AIReadinessGauge score={selectedScan.scan_data?.score} tier={selectedScan.scan_data?.raw?.ai_readiness_tier} />

              <Row>
                <Col md={6}>
                  <h5 className="text-danger">Why this score?</h5>
                  <ListGroup variant="flush" className="mb-3">
                    {selectedScan.scan_data?.reasons?.map((reason, idx) => (
                      <ListGroup.Item
                        key={`reason-${idx}`}
                        className="text-danger-emphasis"
                      >
                        {typeof reason === "string" ? (
                          reason
                        ) : (
                          <>
                            <strong>{reason?.issue || "Issue"}</strong>
                            <br />
                            <span className="text-muted">{reason?.detail}</span>
                          </>
                        )}
                      </ListGroup.Item>
                    )) || <ListGroup.Item>No issues logged.</ListGroup.Item>}
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <h5 className="text-success">Recommended Improvements</h5>
                  <ListGroup variant="flush">
                    {(selectedScan.scan_data?.reasons || selectedScan.scan_data?.improvements)?.map((imp, idx) => {
                      const text = typeof imp === "string" ? imp : imp.fix || imp.reason || "";
                      if (!text) return null;
                      return (
                        <ListGroup.Item
                          key={`imp-${idx}`}
                          className="list-group-item-success"
                        >
                          {text}
                        </ListGroup.Item>
                      );
                    }) || (
                      <ListGroup.Item>
                        No improvements recommended.
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Col>
              </Row>

              {/* Priority Areas */}
              {selectedScan.scan_data?.raw?.priority_areas && (
                <>
                  <hr />
                  <h5 className="text-primary mt-3">Priority Areas</h5>
                  <ListGroup variant="flush" className="mb-3">
                    {selectedScan.scan_data.raw.priority_areas.map(
                      (area, idx) => (
                        <ListGroup.Item key={idx}>
                          <strong>{area?.area}</strong> — Priority:{" "}
                          <span className="fw-bold text-warning">
                            {area?.priority_level}
                          </span>
                          , Impact:{" "}
                          <span className="fw-bold text-info">
                            {area?.impact}
                          </span>
                          <br />
                          <span className="text-muted">
                            {area?.recommended_action}
                          </span>
                        </ListGroup.Item>
                      ),
                    )}
                  </ListGroup>
                </>
              )}

              {/* Business: Competitor Comparison */}
              {selectedScan.scan_data?.raw?.competitor_comparison && (
                <>
                  <hr />
                  <h5 className="text-primary mt-3">Competitor Comparison</h5>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Website</th>
                        <th>AI Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <strong>{selectedScan.website}</strong>
                        </td>
                        <td>{selectedScan.scan_data?.score}</td>
                      </tr>
                      {selectedScan.scan_data.raw.competitor_comparison.map(
                        (comp, idx) => (
                          <tr key={idx}>
                            <td>{comp?.website}</td>
                            <td>{comp?.score}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Business: Strengths & Weaknesses */}
              {(selectedScan.scan_data?.raw?.strengths ||
                selectedScan.scan_data?.raw?.weaknesses) && (
                <>
                  {selectedScan.scan_data.raw.strengths && (
                    <>
                      <h5 className="mt-3 text-success">Strengths</h5>
                      <ul>
                        {selectedScan.scan_data.raw.strengths.map((s, idx) => (
                          <li key={idx}>{s}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {selectedScan.scan_data.raw.weaknesses && (
                    <>
                      <h5 className="mt-3 text-danger">Weaknesses</h5>
                      <ul>
                        {selectedScan.scan_data.raw.weaknesses.map((w, idx) => (
                          <li key={idx}>{w}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}

              {/* Business: Advanced Fixes */}
              {(selectedScan.scan_data?.raw?.advanced_suggestions ||
                selectedScan.scan_data?.raw?.roadmap_90_days) && (
                <>
                  {selectedScan.scan_data.raw.advanced_suggestions && (
                    <>
                      <h5 className="mt-3 text-primary">
                        Advanced Fix Suggestions
                      </h5>
                      <ListGroup variant="flush" className="mb-3">
                        {selectedScan.scan_data.raw.advanced_suggestions.map(
                          (fix, idx) => (
                            <ListGroup.Item key={idx}>
                              <strong>Fix:</strong> {fix?.fix}
                              <br />
                              <strong>Example:</strong> {fix?.example}
                            </ListGroup.Item>
                          ),
                        )}
                      </ListGroup>
                    </>
                  )}

                  {selectedScan.scan_data.raw.roadmap_90_days && (
                    <>
                      <h5 className="mt-3 text-primary">90-Day Roadmap</h5>
                      <ol>
                        {selectedScan.scan_data.raw.roadmap_90_days.map(
                          (step, idx) => (
                            <li key={idx}>{step}</li>
                          ),
                        )}
                      </ol>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#f8f9fa" }}>
          <Button variant="secondary" onClick={handleCloseDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


export default ScanHistory;
